import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { radius } from '../../theme/radius';

export type PillTone = 'primary' | 'neutral' | 'success' | 'warning' | 'error';

export interface PillProps {
  /** Pill content — a short label (e.g. "~160 cr", "Active"). */
  children: ReactNode;
  /** Color scheme. Defaults to the brand primary. */
  tone?: PillTone;
  /** Additional class name. */
  className?: string;
}

const toneColors = (
  token: GlobalToken,
  tone: PillTone
): { color: string; border: string } => {
  const map: Record<PillTone, { color: string; border: string }> = {
    primary: { color: token.colorPrimary, border: token.colorPrimaryBorder },
    neutral: { color: token.colorTextSecondary, border: token.colorBorder },
    success: { color: token.colorSuccess, border: token.colorSuccessBorder },
    warning: { color: token.colorWarning, border: token.colorWarningBorder },
    error: { color: token.colorError, border: token.colorErrorBorder },
  };
  return map[tone];
};

interface StyledPillProps {
  $token: GlobalToken;
  $tone: PillTone;
}

const StyledPill = styled.span<StyledPillProps>`
  display: inline-flex;
  align-items: center;
  flex: none;
  padding: 1px 8px;
  border-radius: ${radius.pill}px;
  border: ${({ $token, $tone }) =>
    `${$token.lineWidth}px solid ${toneColors($token, $tone).border}`};
  color: ${({ $token, $tone }) => toneColors($token, $tone).color};
  background: transparent;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  white-space: nowrap;
`;

/**
 * Pill — a compact, rounded outline label (e.g. a credit cost or a status
 * word). Non-interactive; for interactive/selectable chips use `Tag`, and for
 * numeric counts / status dots use `Badge`.
 *
 * @example
 * ```tsx
 * import { Pill } from '@libertify/ui';
 *
 * <Pill>~160 cr</Pill>
 * <Pill tone="success">Active</Pill>
 * ```
 */
export const Pill = ({ children, tone = 'primary', className }: PillProps) => {
  const { token } = theme.useToken();
  return (
    <StyledPill $token={token} $tone={tone} className={className}>
      {children}
    </StyledPill>
  );
};

export default Pill;
